import React from 'react';
import { usePlayerCache } from '../contexts/PlayerCacheContext';
import { formatTime } from '../utils/dateFormat';
import CachedAvatar from './CachedAvatar';
import ProfileLink from './ProfileLink';
import './GameInfoModal.css';

export default function GameInfoModal({ gameNum, gameState, serviceState, playerScores, player1Name, player2Name, player1Id, player2Id, team1Players, team2Players, matchType, onClose }) {
  const { getPlayerName, getPlayerPhotoUrl, getPlayerApartmentFlat } = usePlayerCache();

  // Resolve photo URL(s): for singles one per side; for doubles both players per team
  const player1PhotoUrls = matchType === 'doubles'
    ? (team1Players || []).map(id => getPlayerPhotoUrl(id)).filter(Boolean)
    : (getPlayerPhotoUrl(player1Id) ? [getPlayerPhotoUrl(player1Id)] : []);
  const player2PhotoUrls = matchType === 'doubles'
    ? (team2Players || []).map(id => getPlayerPhotoUrl(id)).filter(Boolean)
    : (getPlayerPhotoUrl(player2Id) ? [getPlayerPhotoUrl(player2Id)] : []);

  if (!serviceState) return null;

  const getServerName = (server) => {
    return server === 'player1' ? player1Name : player2Name;
  };

  // Helper to get server name - use serverPlayerId if available (new format), otherwise fallback to teamServer (old format)
  const getServerNameForService = (service, matchType) => {
    if (matchType === 'doubles') {
      // New format: use serverPlayerId
      if (service.serverPlayerId) {
        return getPlayerName(service.serverPlayerId);
      }
      // Old format: use teamServer (for backward compatibility)
      if (service.teamServer) {
        if (service.teamServer === 'team1Player1' && team1Players?.[0]) return getPlayerName(team1Players[0]);
        if (service.teamServer === 'team1Player2' && team1Players?.[1]) return getPlayerName(team1Players[1]);
        if (service.teamServer === 'team2Player1' && team2Players?.[0]) return getPlayerName(team2Players[0]);
        if (service.teamServer === 'team2Player2' && team2Players?.[1]) return getPlayerName(team2Players[1]);
      }
      // Fallback to team name
      return service.server === 'player1' ? player1Name : player2Name;
    } else {
      return service.server === 'player1' ? player1Name : player2Name;
    }
  };

  // Helper to get receiver name - use receiverPlayerId if available (new format), otherwise fallback to teamReceiver (old format)
  const getReceiverNameForService = (service, matchType) => {
    if (matchType === 'doubles') {
      // New format: use receiverPlayerId
      if (service.receiverPlayerId) {
        return getPlayerName(service.receiverPlayerId);
      }
      // Old format: use teamReceiver (for backward compatibility)
      if (service.teamReceiver) {
        if (service.teamReceiver === 'team1Player1' && team1Players?.[0]) return getPlayerName(team1Players[0]);
        if (service.teamReceiver === 'team1Player2' && team1Players?.[1]) return getPlayerName(team1Players[1]);
        if (service.teamReceiver === 'team2Player1' && team2Players?.[0]) return getPlayerName(team2Players[0]);
        if (service.teamReceiver === 'team2Player2' && team2Players?.[1]) return getPlayerName(team2Players[1]);
      }
      return 'Unknown';
    }
    return null;
  };

  const getPointWinnerName = (service, matchType) => {
    // For doubles, use winnerPlayerId if available (new format)
    if (matchType === 'doubles' && service.winnerPlayerId) {
      return getPlayerName(service.winnerPlayerId);
    }
    // Fallback to old format or singles
    if (!service.winner && !service.player) return 'Unknown';
    const winner = service.winner || service.player;
    if (matchType === 'doubles') {
      // For doubles, winner indicates which team won (player1 = team1, player2 = team2)
      if (winner === 'player1') {
        return player1Name; // Team 1
      } else {
        return player2Name; // Team 2
      }
    } else {
      // For singles, winner indicates which player won
      return winner === 'player1' ? player1Name : player2Name;
    }
  };

  const getPointLoserName = (service, matchType) => {
    // For doubles, use loserPlayerId if available (new format)
    if (matchType === 'doubles' && service.loserPlayerId) {
      return getPlayerName(service.loserPlayerId);
    }
    // Fallback to old format
    if (!service.loser) {
      // Derive from winner
      const winner = service.winner || service.player;
      const loser = winner === 'player1' ? 'player2' : 'player1';
      if (matchType === 'doubles') {
        return loser === 'player1' ? player1Name : player2Name;
      } else {
        return loser === 'player1' ? player1Name : player2Name;
      }
    }
    if (matchType === 'doubles') {
      if (service.loser === 'player1') {
        return player1Name; // Team 1
      } else {
        return player2Name; // Team 2
      }
    } else {
      return service.loser === 'player1' ? player1Name : player2Name;
    }
  };

  // Calculate service statistics by individual player (for doubles) or by team (for singles)
  const serviceStats = {};
  if (matchType === 'doubles') {
    // For doubles, track by individual player ID
    serviceState.services?.forEach(service => {
      const key = service.serverPlayerId || service.teamServer || service.server;
      if (!serviceStats[key]) {
        serviceStats[key] = { count: 0, totalPoints: 0, playerId: service.serverPlayerId || null };
      }
      serviceStats[key].count++;
      serviceStats[key].totalPoints += service.points;
    });
  } else {
    // For singles, track by team (player1/player2)
    serviceState.services?.forEach(service => {
      const key = service.server;
      if (!serviceStats[key]) {
        serviceStats[key] = { count: 0, totalPoints: 0 };
      }
      serviceStats[key].count++;
      serviceStats[key].totalPoints += service.points;
    });
  }

  // Calculate individual player points for doubles
  const playerPoints = {};
  if (playerScores) {
    Object.entries(playerScores).forEach(([playerId, points]) => {
      playerPoints[playerId] = points;
    });
  }

  return (
    <div className="game-info-modal-overlay" onClick={onClose}>
      <div className="game-info-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Game {gameNum} Details</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-content">
          {/* Current Score */}
          <div className="info-section">
            <h3>Current Score</h3>
            <div className="score-display-large">
              <div className="score-item">
                {player1PhotoUrls.length > 0 && (
                  <div className={`score-player-avatars ${player1PhotoUrls.length === 1 ? 'score-player-avatars--single' : ''}`}>
                    {player1PhotoUrls.map((url, i) => (
                      <CachedAvatar key={i} url={url} name={player1Name} className="score-player-avatar" />
                    ))}
                  </div>
                )}
                <span className="score-label">{player1Name}</span>
                <span className="score-value">{gameState?.player1 || 0}</span>
              </div>
              <div className="score-separator">-</div>
              <div className="score-item">
                {player2PhotoUrls.length > 0 && (
                  <div className={`score-player-avatars ${player2PhotoUrls.length === 1 ? 'score-player-avatars--single' : ''}`}>
                    {player2PhotoUrls.map((url, i) => (
                      <CachedAvatar key={i} url={url} name={player2Name} className="score-player-avatar" />
                    ))}
                  </div>
                )}
                <span className="score-label">{player2Name}</span>
                <span className="score-value">{gameState?.player2 || 0}</span>
              </div>
            </div>
          </div>

          {/* Current Server */}
          <div className="info-section">
            <h3>Current Server</h3>
            <div className="current-server">
              <span className={`server-badge ${serviceState.currentServer === 'player1' ? 'active' : ''}`}>
                {matchType === 'doubles' && serviceState.currentServerPlayerId
                  ? getPlayerName(serviceState.currentServerPlayerId)
                  : getServerName(serviceState.currentServer)}
              </span>
            </div>
          </div>

          {/* Service History */}
          <div className="info-section">
            <h3>Service History</h3>
            {serviceState.services && serviceState.services.length > 0 ? (
              <div className="service-list">
                {serviceState.services.map((service, index) => {
                  const serverName = getServerNameForService(service, matchType);
                  const receiverName = getReceiverNameForService(service, matchType);
                  const winnerName = getPointWinnerName(service, matchType);
                  const loserName = getPointLoserName(service, matchType);
                  const servesCompleted = service.servesCompleted || service.points || 1;
                  const serverPoints = service.serverPointsAtService !== undefined 
                    ? service.serverPointsAtService 
                    : (serviceState.currentServer === service.server ? (gameState?.[service.server] || 0) - service.points : 0);
                  
                  return (
                    <div key={index} className="service-item">
                      <span className="service-number">#{index + 1}</span>
                      <div className="service-details">
                        <div className="service-row">
                          <span className="service-label">Player serving:</span>
                          <span className="service-value service-server-name">{serverName}</span>
                        </div>
                        {matchType === 'doubles' && receiverName && (
                          <div className="service-row">
                            <span className="service-label">Served to:</span>
                            <span className="service-value service-receiver-name">{receiverName}</span>
                          </div>
                        )}
                        <div className="service-row">
                          <span className="service-label">Player winning service:</span>
                          <span className="service-value service-winner">{winnerName}</span>
                        </div>
                        <div className="service-row">
                          <span className="service-label">Serves completed:</span>
                          <span className="service-value service-serves-badge">{servesCompleted}</span>
                        </div>
                        <div className="service-row">
                          <span className="service-label">Player losing service:</span>
                          <span className="service-value service-loser">{loserName}</span>
                        </div>
                        <div className="service-row">
                          <span className="service-label">Server's points at service:</span>
                          <span className="service-value service-points-historic">{serverPoints}</span>
                        </div>
                        {service.timestamp && (
                          <div className="service-row">
                            <span className="service-label">Time:</span>
                            <span className="service-value service-time">{formatTime(service.timestamp)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="no-data">No services recorded yet</p>
            )}
          </div>

          {/* Service Statistics */}
          <div className="info-section">
            <h3>Service Statistics</h3>
            <div className="service-stats">
              <div className="stat-item">
                <span className="stat-label">{player1Name}</span>
                <span className="stat-value">
                  {serviceStats.player1?.count || 0} services, {serviceStats.player1?.totalPoints || 0} points
                </span>
              </div>
              <div className="stat-item">
                <span className="stat-label">{player2Name}</span>
                <span className="stat-value">
                  {serviceStats.player2?.count || 0} services, {serviceStats.player2?.totalPoints || 0} points
                </span>
              </div>
            </div>
          </div>

          {/* Individual Player Scores (Doubles) */}
          {matchType === 'doubles' && playerScores && Object.keys(playerScores).length > 0 && (
            <div className="info-section">
              <h3>Individual Player Points</h3>
              <div className="player-scores-list">
                {Object.entries(playerScores).map(([playerId, points]) => (
                  <div key={playerId} className="player-score-item">
                    <span>
                      <ProfileLink userId={playerId} className="player-name-link">
                        <span className="player-name">{getPlayerName(playerId)}</span>
                      </ProfileLink>
                      {getPlayerApartmentFlat(playerId) && (
                        <span className="player-apartment-flat" style={{ display: 'block', fontSize: '0.85em', color: '#666' }}>{getPlayerApartmentFlat(playerId)}</span>
                      )}
                    </span>
                    <span className="player-points">{points} points</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

